import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

export const supportedLngs = ['zh-Hans', 'zh-Hant', 'th-TH', 'vi-VN'] as const
export type SupportedLanguage = (typeof supportedLngs)[number]

const localeAliases: Record<string, SupportedLanguage> = {
  zh: 'zh-Hans',
  'zh-cn': 'zh-Hans',
  'zh-hans': 'zh-Hans',
  'zh-tw': 'zh-Hant',
  'zh-hant': 'zh-Hant',
  th: 'th-TH',
  'th-th': 'th-TH',
  vi: 'vi-VN',
  'vi-vn': 'vi-VN'
}

/**
 * Normalizes the BCP-47 tags accepted by market configuration and device
 * settings to the catalog names bundled with this application.
 *
 * The fallback intentionally remains BaoBi's current Simplified Chinese
 * presentation. A new operator must not enable a locale until its full
 * catalog and UI review have passed.
 */
export function normalizeSupportedLanguage(locale: string | undefined): SupportedLanguage | undefined {
  if (!locale) return undefined

  return localeAliases[locale.toLowerCase()]
}

export const defaultLng = normalizeSupportedLanguage(process.env.EXPO_PUBLIC_DEFAULT_LOCALE) ?? 'zh-Hans'

// ref: https://gist.github.com/0x5424/4d47e1cd44b95af224d2fb9990568943
const resolveLocales = (ctx: __MetroModuleApi.RequireContext) => {
  const resolved: { resources: Record<string, any>; ns: string[] } = { resources: {}, ns: [] }
  const regex = /(?<context>\w*)\/(?<locale>[\w-]+)\/(?<namespace>\w+)\.json$/

  ctx.keys().forEach((file) => {
    const matched = file.match(regex)
    if (matched && matched.groups) {
      const { locale, namespace } = matched.groups
      resolved.ns.push(namespace)
      resolved.resources[locale] = resolved.resources[locale] ? resolved.resources[locale] : {}
      resolved.resources[locale][namespace] = ctx(file)
    }
  })

  resolved.ns = [...new Set(resolved.ns)]

  return resolved
}

const locales = resolveLocales(require.context('../../assets/locales', true, /\w+\.json$/))

i18n.use(initReactI18next).init({
  compatibilityJSON: 'v3',
  ns: locales.ns,
  resources: locales.resources,
  defaultNS: ['common'],
  supportedLngs,
  lng: defaultLng,
  fallbackLng: 'zh-Hans',
  load: 'currentOnly',
  interpolation: {
    escapeValue: false
  }
})

export async function setAppLanguage(locale: string) {
  const language = normalizeSupportedLanguage(locale)
  if (!language) {
    throw new Error(`Unsupported application locale: ${locale}`)
  }

  await i18n.changeLanguage(language)
}

export default i18n
